import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Home, QrCode, Edit, Trash2, Calendar, MapPin, Box, Activity, Camera, Image, Video, X, Download, FileText, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toggle } from '../components/ui/toggle';
import { toast } from 'sonner';
import QRCodeGenerator from '../components/QRCodeGenerator';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MATERIALS = ['Wood', 'PlyWood', 'Plastic', 'Bucket', 'Other'];
const FRAME_TYPES = ['Foundationless', 'Wax Starter Strip', 'Wax Foundation', 'Drawn Comb', 'Plastic Foundation', 'Layens'];
const FRAME_SIZES = ['Deep', 'Medium', 'Shallow', 'Ideal', 'Layens', 'Other'];
const LURE_TYPES = ['None', 'Lemon Grass', 'Swarm Commander', 'Old Brood Comb', 'Beeswax', 'Other'];
const SCOUT_LEVELS = ['0 - 10', '10 - 50', '50 or more'];
const CLUSTER_LOCATIONS = ['Tree Branch', 'Fence', 'Roof Eves', 'Balcony', 'Car', 'Road', 'House', 'Shed floor', 'Inside Shed', 'Building', 'Other'];
const HEIGHT_OPTIONS = ['Ground', '1m', '2m', '3m', '4m and above'];
const MEASUREMENT_SYSTEMS = [
  { value: 'metric', label: 'Metric (cm, L)' },
  { value: 'imperial', label: 'Imperial (inches, gal)' }
];

// Calculate volume based on measurement system
const calculateVolume = (width, height, length, system = 'metric') => {
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const l = parseFloat(length) || 0;
  if (w && h && l) {
    if (system === 'imperial') {
      // cubic inches to liters: 1 cubic inch = 0.0163871 liters
      const volumeInLiters = (w * h * l) * 0.0163871;
      return volumeInLiters.toFixed(1);
    } else {
      // cubic cm to liters: 1000 cubic cm = 1 liter
      const volumeInLiters = (w * h * l) / 1000;
      return volumeInLiters.toFixed(1);
    }
  }
  return '';
};

const SwarmTraps = () => {
  const navigate = useNavigate();
  const [traps, setTraps] = useState([]);
  const [yards, setYards] = useState([]);
  const [hives, setHives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [selectedTrap, setSelectedTrap] = useState(null);
  const [editingTrap, setEditingTrap] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [showDashboard, setShowDashboard] = useState(true);
  const [activePerformanceTab, setActivePerformanceTab] = useState('recommendations');
  
  // Rescued Swarms state
  const [rescuedSwarms, setRescuedSwarms] = useState([]);
  const [showRescueDialog, setShowRescueDialog] = useState(false);
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [selectedRescueForOutcome, setSelectedRescueForOutcome] = useState(null);
  const [editingRescue, setEditingRescue] = useState(null);
  const [rescueFormData, setRescueFormData] = useState({
    location: '',
    cluster_location: '',
    cluster_location_other: '',
    height_off_ground: '',
    date: '',
    time: '',
    notes: '',
    outcome: '',
    destination_hive_id: '',
    photos: [],
    videos: []
  });
  const [outcomeFormData, setOutcomeFormData] = useState({
    outcome: '',
    destination_hive_id: ''
  });
  
  // Quick Add Hive state
  const [showQuickAddHive, setShowQuickAddHive] = useState(false);
  const [quickAddHiveData, setQuickAddHiveData] = useState({
    name: '',
    type: 'hive', // 'hive' or 'nuc'
    yard: ''
  });

  const [formData, setFormData] = useState({
    name: '',
    yard: '',
    location: '',
    measurement_system: 'metric',
    width: '',
    height: '',
    length: '',
    volume: '',
    material: '',
    material_other: '',
    entrance_size: '',
    frame_entries: [], // Array of {type, size, count}
    frame_type: '',
    frame_size: '',
    frame_count: '',
    swarm_lure: '',
    swarm_lure_other: '',
    date_placed: '',
    activity_records: [],
    swarm_arrived_date: '',
    swarm_arrived_time: '',
    rescued_swarm: {
      location: '',
      date: '',
      time: '',
      cluster_location: '',
      cluster_location_other: '',
      height_off_ground: '',
      photos: [],
      videos: []
    },
    status: 'active'
  });

  // Auto-calculate volume when dimensions change
  useEffect(() => {
    const newVolume = calculateVolume(formData.width, formData.height, formData.length, formData.measurement_system);
    if (newVolume && newVolume !== formData.volume) {
      setFormData(prev => ({ ...prev, volume: newVolume }));
    }
  }, [formData.width, formData.height, formData.length, formData.measurement_system]);

  const [newActivity, setNewActivity] = useState({
    date: '',
    time: '',
    scouts_level: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token && localStorage.getItem('demoMode') === 'true') {
        setLoading(false);
        return;
      }
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [trapsRes, yardsRes, hivesRes, summaryRes, performanceRes, rescuesRes] = await Promise.all([
        axios.get(`${API}/swarm-traps`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/yards`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/hives`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/swarm-traps/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/swarm-traps/performance`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/rescued-swarms`, { headers }).catch(() => ({ data: [] }))
      ]);
      setTraps(trapsRes.data);
      setYards(yardsRes.data);
      setHives(hivesRes.data);
      setSummaryData(summaryRes.data);
      setPerformanceData(performanceRes.data);
      setRescuedSwarms(rescuesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Rescued Swarm functions
  const resetRescueForm = () => {
    setRescueFormData({
      location: '',
      cluster_location: '',
      cluster_location_other: '',
      height_off_ground: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      notes: '',
      outcome: '',
      destination_hive_id: '',
      photos: [],
      videos: []
    });
    setEditingRescue(null);
  };

  const handleRescueMediaUpload = async (e, mediaType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const currentMedia = rescueFormData[mediaType] || [];
      setRescueFormData({
        ...rescueFormData,
        [mediaType]: [...currentMedia, res.data.url]
      });
      toast.success(`${mediaType === 'photos' ? 'Photo' : 'Video'} uploaded`);
    } catch (error) {
      toast.error('Failed to upload media');
    }
  };

  const removeRescueMedia = (mediaType, idx) => {
    const currentMedia = [...(rescueFormData[mediaType] || [])];
    currentMedia.splice(idx, 1);
    setRescueFormData({
      ...rescueFormData,
      [mediaType]: currentMedia
    });
  };

  const saveRescuedSwarm = async () => {
    if (!rescueFormData.location) {
      toast.error('Please enter a rescue location');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingRescue) {
        await axios.put(`${API}/rescued-swarms/${editingRescue.id}`, rescueFormData, { headers });
        toast.success('Rescue record updated');
      } else {
        await axios.post(`${API}/rescued-swarms`, rescueFormData, { headers });
        toast.success('Rescue logged successfully');
      }
      
      setShowRescueDialog(false);
      resetRescueForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save rescue record');
    }
  };

  const deleteRescuedSwarm = async (id) => {
    if (!window.confirm('Delete this rescue record?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/rescued-swarms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rescue record deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rescue record');
    }
  };

  const saveOutcome = async () => {
    if (!selectedRescueForOutcome || !outcomeFormData.outcome) {
      toast.error('Please select an outcome');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/rescued-swarms/${selectedRescueForOutcome.id}`, {
        ...selectedRescueForOutcome,
        outcome: outcomeFormData.outcome,
        destination_hive_id: outcomeFormData.destination_hive_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Outcome updated');
      setShowOutcomeDialog(false);
      setSelectedRescueForOutcome(null);
      setOutcomeFormData({ outcome: '', destination_hive_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to update outcome');
    }
  };

  // Quick Add Hive/NUC function
  const saveQuickAddHive = async () => {
    if (!quickAddHiveData.name) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const isNuc = outcomeFormData.outcome === 'transferred_to_nuc' || quickAddHiveData.type === 'nuc';
      
      // Extract hive number from name if it contains a number, otherwise use next available
      const nameMatch = quickAddHiveData.name.match(/\d+/);
      const hiveNumber = nameMatch ? parseInt(nameMatch[0]) : (Math.max(0, ...hives.map(h => h.hive_number || 0)) + 1);
      
      const hiveData = {
        hive_number: hiveNumber,
        nickname: quickAddHiveData.name,
        yard: quickAddHiveData.yard || yards[0]?.name || '',
        location: '',
        notes: `Created from swarm capture on ${new Date().toLocaleDateString()}`,
        hive_stack: {
          hive_type: isNuc ? 'nuc' : 'langstroth'
        },
        status: 'active',
        origin_type: 'Swarm Capture',
        origin_date: new Date().toISOString().split('T')[0]
      };
      
      const response = await axios.post(`${API}/hives`, hiveData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newHiveId = response.data.id;
      const newHiveName = quickAddHiveData.name;
      
      // Close the Quick Add dialog first
      setShowQuickAddHive(false);
      setQuickAddHiveData({ name: '', type: 'hive', yard: '' });
      
      // Now auto-save the outcome with the new hive
      if (selectedRescueForOutcome) {
        await axios.put(`${API}/rescued-swarms/${selectedRescueForOutcome.id}`, {
          ...selectedRescueForOutcome,
          outcome: outcomeFormData.outcome,
          destination_hive_id: newHiveId
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        toast.success(`${isNuc ? 'NUC' : 'Hive'} "${newHiveName}" created and swarm moved!`);
        
        // Close the outcome dialog and reset
        setShowOutcomeDialog(false);
        setSelectedRescueForOutcome(null);
        setOutcomeFormData({ outcome: '', destination_hive_id: '' });
      } else {
        toast.success(`${isNuc ? 'NUC' : 'Hive'} created successfully`);
      }
      
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error('Failed to create hive');
    }
  };

  // Export to CSV
  const exportCSV = () => {
    if (!traps.length) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Name', 'Yard', 'Location', 'Dimensions (WxHxL)', 'Volume (L)', 'Material', 'Lure', 'Status', 'Swarm Arrived Date', 'Activity Count', 'Created At'];
    const rows = traps.map(trap => [
      trap.name || '',
      trap.yard || '',
      trap.location || '',
      `${trap.width || ''}x${trap.height || ''}x${trap.length || ''}`,
      trap.volume || '',
      trap.material === 'Other' ? trap.material_other : (trap.material || ''),
      trap.swarm_lure === 'Other' ? trap.swarm_lure_other : (trap.swarm_lure || ''),
      trap.status || 'active',
      trap.swarm_arrived_date || '',
      (trap.activity_records || []).length,
      trap.created_at ? new Date(trap.created_at).toLocaleDateString() : ''
    ]);
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `swarm_traps_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  // Export to PDF
  const exportPDF = async () => {
    if (!traps.length) {
      toast.error('No data to export');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/swarm-traps/export-pdf`,
        { trap_ids: traps.map(t => t.id) },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `swarm_traps_report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      yard: '',
      location: '',
      measurement_system: 'metric',
      width: '',
      height: '',
      length: '',
      volume: '',
      material: '',
      material_other: '',
      entrance_size: '',
      frame_entries: [],
      frame_type: '',
      frame_size: '',
      frame_count: '',
      swarm_lure: '',
      swarm_lure_other: '',
      date_placed: '',
      activity_records: [],
      swarm_arrived_date: '',
      swarm_arrived_time: '',
      rescued_swarm: { location: '', date: '', time: '', cluster_location: '', cluster_location_other: '', height_off_ground: '', photos: [], videos: [] },
      status: 'active'
    });
    setNewActivity({ date: '', time: '', scouts_level: '', notes: '' });
  };

  const handleAddTrap = async () => {
    if (!formData.name) {
      toast.error('Please enter a trap name');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/swarm-traps`, formData, { headers });
      toast.success('Swarm trap added');
      resetForm();
      setShowAddDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add trap');
    }
  };

  const handleUpdateTrap = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/swarm-traps/${editingTrap.id}`, formData, { headers });
      toast.success('Swarm trap updated');
      resetForm();
      setEditingTrap(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update trap');
    }
  };

  const handleDeleteTrap = async (trapId) => {
    if (!window.confirm('Are you sure you want to delete this trap?')) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/swarm-traps/${trapId}`, { headers });
      toast.success('Swarm trap deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete trap');
    }
  };

  const handleEditTrap = (trap) => {
    setFormData({
      ...trap,
      frame_size: trap.frame_size || '',
      rescued_swarm: trap.rescued_swarm || { location: '', date: '', time: '', photos: [], videos: [] }
    });
    setEditingTrap(trap);
  };

  const addActivityRecord = () => {
    if (!newActivity.date || !newActivity.scouts_level) {
      toast.error('Please fill date and scouts level');
      return;
    }
    setFormData({
      ...formData,
      activity_records: [...formData.activity_records, { ...newActivity, id: Date.now() }]
    });
    setNewActivity({ date: '', time: '', scouts_level: '', notes: '' });
  };

  const removeActivityRecord = (id) => {
    setFormData({
      ...formData,
      activity_records: formData.activity_records.filter(a => a.id !== id)
    });
  };

  // Handle photo/video upload (from camera or file)
  const handleMediaUpload = async (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload`, uploadData, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const mediaUrl = `${API}/uploads/${res.data.file_id}`;
      
      const currentMedia = formData.rescued_swarm?.[mediaType] || [];
      setFormData({
        ...formData,
        rescued_swarm: {
          ...formData.rescued_swarm,
          [mediaType]: [...currentMedia, mediaUrl]
        }
      });
      toast.success(`${mediaType === 'photos' ? 'Photo' : 'Video'} uploaded`);
    } catch (error) {
      toast.error('Failed to upload media');
    }
  };

  const removeMedia = (mediaType, index) => {
    const currentMedia = [...(formData.rescued_swarm?.[mediaType] || [])];
    currentMedia.splice(index, 1);
    setFormData({
      ...formData,
      rescued_swarm: {
        ...formData.rescued_swarm,
        [mediaType]: currentMedia
      }
    });
  };

  // Move to Hive state
  const [showMoveToHiveDialog, setShowMoveToHiveDialog] = useState(false);
  const [moveToHiveTrap, setMoveToHiveTrap] = useState(null);

  const readyHives = hives.filter(h => h.status === 'ready');

  const handleConvertToHive = async (trap) => {
    if (readyHives.length > 0) {
      setMoveToHiveTrap(trap);
      setShowMoveToHiveDialog(true);
    } else {
      // No ready hives — go straight to Add Hive
      navigate('/', { state: { openAddHive: true, trapData: trap } });
    }
  };

  const handleAssignToReadyHive = async (hiveId) => {
    const trap = moveToHiveTrap;
    if (!trap || !hiveId) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const selectedHive = hives.find(h => h.id === hiveId);
      await axios.put(`${API}/hives/${hiveId}`, {
        hive_number: selectedHive?.hive_number || 0,
        yard: selectedHive?.yard || '',
        nickname: selectedHive?.nickname || '',
        status: 'active',
        origin_type: 'Swarm Trap',
        origin_date: trap.swarm_arrived_date || new Date().toISOString(),
        origin_location: trap.location || '',
        bee_variety: trap.bee_type || '',
        notes: `Swarm captured from trap "${trap.name}"${trap.location ? ` at ${trap.location}` : ''}. ${selectedHive?.notes || ''}`.trim(),
      }, { headers });

      // Mark trap as converted
      await axios.put(`${API}/swarm-traps/${trap.id}`, {
        ...trap,
        converted_to_hive_id: hiveId,
        status: 'inactive',
      }, { headers });

      toast.success(`Swarm assigned to Hive #${selectedHive?.hive_number}${selectedHive?.nickname ? ` (${selectedHive.nickname})` : ''}`);
      setShowMoveToHiveDialog(false);
      setMoveToHiveTrap(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to assign swarm to hive');
    }
  };

  if (loading) {
    return (
      <div className="bg-stone-50 flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen honeycomb-bg pb-24">
      {/* Header */}
      <div className="p-4">
        <div className="max-w-7xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Target className="h-6 w-6 text-amber-600" /> Swarm Traps & Rescues</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {traps.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCSV}
                  className="text-teal-700 border-teal-300 hover:bg-teal-50"
                >
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportPDF}
                  className="text-teal-700 border-teal-300 hover:bg-teal-50"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </>
            )}
            <Button
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-amber-600 hover:bg-amber-700 text-white h-12 px-6 font-bold rounded-full"
            >
              <Plus className="mr-2 h-5 w-5" />
              Add Trap
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl p-4 md:p-8">
        {/* Dashboard Summary Section */}
        {summaryData && summaryData.total_traps > 0 && (
          <div className="mb-8">
            <div className="bg-teal-50 rounded-2xl p-5 border border-teal-200">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <div className="bg-white rounded-xl p-4 border border-teal-100 text-center shadow-sm">
                  <div className="text-3xl font-bold text-teal-700">{summaryData.total_traps}</div>
                  <div className="text-sm text-teal-600">Total Traps</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-green-100 text-center shadow-sm">
                  <div className="text-3xl font-bold text-green-700">{summaryData.active_traps}</div>
                  <div className="text-sm text-green-600">Active</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-amber-100 text-center shadow-sm">
                  <div className="text-3xl font-bold text-amber-700">{summaryData.catches_this_season}</div>
                  <div className="text-sm text-amber-600">Catches This Season</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-purple-100 text-center shadow-sm">
                  <div className="text-3xl font-bold text-purple-700">{summaryData.success_rate}%</div>
                  <div className="text-sm text-purple-600">Success Rate</div>
                </div>
              </div>
              
              {/* Best Performing Configuration Card */}
              {performanceData?.best_configuration && (
                <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 rounded-xl p-5 border-2 border-amber-300 mb-5 shadow-md" data-testid="best-config-card">
                  <div className="flex items-start gap-4">
                    <div className="text-4xl">🏆</div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-amber-800 mb-2">Best Performing Configuration</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div className="bg-white/70 rounded-lg p-3 border border-amber-200">
                          <div className="text-xs text-amber-600 font-medium mb-1">🪵 Material</div>
                          <div className="font-bold text-stone-800">{performanceData.best_configuration.material}</div>
                        </div>
                        <div className="bg-white/70 rounded-lg p-3 border border-amber-200">
                          <div className="text-xs text-amber-600 font-medium mb-1">🍋 Lure Type</div>
                          <div className="font-bold text-stone-800">{performanceData.best_configuration.lure}</div>
                        </div>
                        <div className="bg-white/70 rounded-lg p-3 border border-amber-200">
                          <div className="text-xs text-amber-600 font-medium mb-1">📦 Volume</div>
                          <div className="font-bold text-stone-800">{performanceData.best_configuration.volume_range}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                          ✓ {performanceData.best_configuration.success_rate}% Success Rate
                        </span>
                        <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-medium">
                          🐝 {performanceData.best_configuration.total_catches} Catches
                        </span>
                        <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full font-medium">
                          📊 {performanceData.best_configuration.total_traps} Traps
                        </span>
                        {performanceData.best_configuration.total_converted > 0 && (
                          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">
                            🏠 {performanceData.best_configuration.total_converted} Converted
                          </span>
                        )}
                        {performanceData.best_configuration.avg_days_to_catch != null && (
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                            ⏱️ ~{performanceData.best_configuration.avg_days_to_catch} days to catch
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Catches Timeline Chart */}
              {summaryData.catches_timeline && summaryData.catches_timeline.length > 0 && (
                <div className="bg-white rounded-xl p-4 border border-teal-100 mb-5 shadow-sm">
                  <h4 className="text-sm font-semibold text-teal-800 mb-3">📊 Catches Over Time</h4>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={summaryData.catches_timeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          tickFormatter={(value) => {
                            const [year, month] = value.split('-');
                            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return monthNames[parseInt(month) - 1] || value;
                          }}
                        />
                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
                        <Tooltip 
                          formatter={(value) => [value, 'Catches']}
                          labelFormatter={(value) => {
                            const [year, month] = value.split('-');
                            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            return `${monthNames[parseInt(month) - 1]} ${year}`;
                          }}
                        />
                        <Bar dataKey="catches" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              
              {/* Two Column Layout: Map and Outcomes */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {/* Trap Locations Map */}
                {summaryData.trap_locations && summaryData.trap_locations.some(t => t.gps_lat && t.gps_lng) && (
                  <div className="bg-white rounded-xl p-4 border border-teal-100 shadow-sm">
                    <h4 className="text-sm font-semibold text-teal-800 mb-3">📍 Trap Locations</h4>
                    <div className="h-56 rounded-lg overflow-hidden">
                      <MapContainer
                        center={(() => {
                          const validLocs = summaryData.trap_locations.filter(t => t.gps_lat && t.gps_lng);
                          if (validLocs.length > 0) {
                            const avgLat = validLocs.reduce((sum, t) => sum + t.gps_lat, 0) / validLocs.length;
                            const avgLng = validLocs.reduce((sum, t) => sum + t.gps_lng, 0) / validLocs.length;
                            return [avgLat, avgLng];
                          }
                          return [-38.0, 145.0];
                        })()}
                        zoom={10}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; OpenStreetMap contributors'
                        />
                        {summaryData.trap_locations.filter(t => t.gps_lat && t.gps_lng).map((trap, idx) => (
                          <Marker
                            key={trap.id || idx}
                            position={[trap.gps_lat, trap.gps_lng]}
                            icon={L.divIcon({
                              className: 'custom-marker',
                              html: `<div style="background: ${trap.status === 'captured' ? '#f59e0b' : trap.status === 'converted' ? '#10b981' : '#14b8a6'}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; display: flex; align-items: center; justify-content: center; font-size: 14px; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">🪤</div>`,
                              iconSize: [28, 28],
                              iconAnchor: [14, 14]
                            })}
                          >
                            <Popup>
                              <div className="text-sm">
                                <strong>{trap.name}</strong>
                                <div className="text-xs text-stone-500">
                                  {trap.location_type === 'apiary' ? `At: ${trap.yard}` : trap.location || 'Remote location'}
                                </div>
                                <div className={`text-xs mt-1 ${trap.status === 'captured' ? 'text-amber-600' : trap.status === 'converted' ? 'text-green-600' : 'text-teal-600'}`}>
                                  {trap.status === 'captured' ? '🐝 Swarm Captured' : trap.status === 'converted' ? '✅ Converted to Hive' : '⏳ Active'}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                    <div className="flex justify-center gap-4 mt-3 text-xs">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-teal-500"></span> Active</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span> Captured</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500"></span> Converted</span>
                    </div>
                  </div>
                )}
                
                {/* Outcomes Summary */}
                <div className="bg-white rounded-xl p-4 border border-teal-100 shadow-sm">
                  <h4 className="text-sm font-semibold text-teal-800 mb-3">📈 Catch Outcomes</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-green-700">🏠 Converted to Hive</span>
                      <span className="font-bold text-green-700">{summaryData.outcomes?.converted_to_hive || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm text-blue-700">📦 Transferred to NUC</span>
                      <span className="font-bold text-blue-700">{summaryData.outcomes?.transferred_to_nuc || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                      <span className="text-sm text-red-700">💨 Absconded</span>
                      <span className="font-bold text-red-700">{summaryData.outcomes?.absconded || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                      <span className="text-sm text-stone-700">📋 Other/In Progress</span>
                      <span className="font-bold text-stone-700">{summaryData.outcomes?.other || 0}</span>
                    </div>
                  </div>
                  
                  {/* All Time Stats */}
                  <div className="mt-4 p-4 bg-teal-100/50 rounded-lg">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-teal-700">{summaryData.catches_all_time}</div>
                      <div className="text-sm text-teal-600">Total Catches (All Time)</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Top Performing Traps */}
              {summaryData.top_traps && summaryData.top_traps.length > 0 && (
                <div className="bg-white rounded-xl border border-teal-100 overflow-hidden shadow-sm">
                  <h4 className="text-sm font-semibold text-teal-800 p-4 bg-teal-100/50">🏆 Top Performing Traps</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-teal-50">
                        <tr>
                          <th className="text-left py-3 px-4 text-teal-800">Rank</th>
                          <th className="text-left py-3 px-4 text-teal-800">Trap Name</th>
                          <th className="text-center py-3 px-4 text-teal-800">Catches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.top_traps.map((trap, idx) => (
                          <tr key={trap.name || idx} className={`border-t border-teal-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-teal-50/30'}`}>
                            <td className="py-3 px-4 text-stone-500">
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                            </td>
                            <td className="py-3 px-4 font-medium text-stone-700">{trap.name}</td>
                            <td className="py-3 px-4 text-center">
                              <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                                {trap.catches} {trap.catches === 1 ? 'catch' : 'catches'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-teal-600 text-center py-3 mt-2">
                💡 Tip: Place traps at 2-3m height with lemon grass oil for best results during swarm season (Spring)
              </p>
            </div>
          </div>
        )}
        {traps.length === 0 ? (
          <div className="text-center py-16">
            <Box className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-stone-600">No swarm traps yet</h2>
            <p className="text-stone-500 mt-2">Add your first swarm trap to start monitoring</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {traps.map((trap) => (
              <div
                key={trap.id}
                className={`bg-white border-2 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all ${
                  trap.status === 'captured' ? 'border-lime-400 bg-lime-50' : 'border-stone-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg text-stone-800">{trap.name}</h3>
                    {trap.yard && <p className="text-sm text-stone-500">{trap.yard}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setSelectedTrap(trap); setShowQRDialog(true); }}
                      className="h-8 w-8"
                    >
                      <QrCode className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditTrap(trap)}
                      className="h-8 w-8"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTrap(trap.id)}
                      className="h-8 w-8 text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="text-sm text-stone-600 space-y-1">
                  {trap.width && trap.height && trap.length && (
                    <p>📐 {trap.width}" x {trap.height}" x {trap.length}" {trap.volume && `(${trap.volume}L)`}</p>
                  )}
                  {trap.material && <p>🪵 {trap.material}{trap.material_other && `: ${trap.material_other}`}</p>}
                  {trap.frame_type && trap.frame_type !== 'None' && (
                    <p>🖼️ {trap.frame_type} ({trap.frame_count || 0})</p>
                  )}
                  {trap.swarm_lure && trap.swarm_lure !== 'None' && (
                    <p>🍋 {trap.swarm_lure}{trap.swarm_lure_other && `: ${trap.swarm_lure_other}`}</p>
                  )}
                </div>

                {trap.activity_records?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <p className="text-xs font-semibold text-stone-500 mb-1">Recent Activity</p>
                    <div className="text-sm text-stone-600">
                      {trap.activity_records.slice(-2).map((activity, idx) => (
                        <p key={idx}>🐝 {activity.date}: {activity.scouts_level} scouts</p>
                      ))}
                    </div>
                  </div>
                )}

                {trap.date_placed && (
                  <div className="mt-2 text-xs text-stone-500">
                    📅 Placed: {new Date(trap.date_placed).toLocaleDateString()}
                    {trap.swarm_arrived_date && (() => {
                      const placed = new Date(trap.date_placed);
                      const arrived = new Date(trap.swarm_arrived_date);
                      const days = Math.max(0, Math.round((arrived - placed) / (1000 * 60 * 60 * 24)));
                      return <span className="ml-2 font-medium text-amber-600">({days} days to catch)</span>;
                    })()}
                  </div>
                )}

                {trap.swarm_arrived_date && (
                  <div className="mt-3 bg-lime-100 border border-lime-300 rounded-lg p-2 text-sm">
                    <p className="font-semibold text-lime-800">✅ Swarm Captured!</p>
                    <p className="text-lime-700">{trap.swarm_arrived_date} {trap.swarm_arrived_time}</p>
                    {trap.converted_to_hive_id && (
                      <p className="text-green-700 font-medium mt-1">
                        🏠 Moved to {(() => {
                          const hive = hives.find(h => h.id === trap.converted_to_hive_id);
                          if (!hive) return 'Hive';
                          const num = hive.hive_number ? `Hive #${hive.hive_number}` : '';
                          const name = hive.nickname || '';
                          return num && name ? `${num} — ${name}` : num || name || 'Hive';
                        })()}
                      </p>
                    )}
                  </div>
                )}

                {trap.swarm_arrived_date && !trap.converted_to_hive_id && (
                  <Button
                    onClick={() => handleConvertToHive(trap)}
                    className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Add to Hives
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Performance Comparison Section */}
        {performanceData && traps.length > 0 && (
          <div className="mt-8">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200">
              <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
                <Target className="h-5 w-5" />
                🎯 Compare Trap Performance
              </h3>
              
              {/* Recommendations Cards */}
              {performanceData.recommendations && performanceData.recommendations.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  {performanceData.recommendations.map((rec, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm">
                      <div className="text-2xl mb-2">{rec.icon}</div>
                      <h4 className="font-semibold text-stone-800 text-sm">{rec.title}</h4>
                      <p className="text-xs text-stone-500 mt-1">{rec.description}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Performance Tabs */}
              <div className="bg-white rounded-xl border border-amber-100 overflow-hidden shadow-sm">
                <div className="flex border-b border-amber-100 overflow-x-auto">
                  {[
                    { id: 'material', label: '🪵 By Material' },
                    { id: 'lure', label: '🍋 By Lure' },
                    { id: 'location', label: '📍 By Location' },
                    { id: 'volume', label: '📦 By Size' }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActivePerformanceTab(tab.id)}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activePerformanceTab === tab.id 
                          ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500' 
                          : 'text-stone-600 hover:bg-amber-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                
                <div className="p-4">
                  {/* Material Performance */}
                  {activePerformanceTab === 'material' && (
                    <div>
                      <h4 className="font-semibold text-stone-700 mb-3">Material Performance Analysis</h4>
                      {performanceData.by_material?.length > 0 ? (
                        <div className="space-y-2">
                          {performanceData.by_material.map((item, idx) => (
                            <div key={item.name} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                              <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🪵'}</span>
                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-stone-700">{item.name}</span>
                                  <span className={`text-sm font-bold ${item.success_rate > 50 ? 'text-green-600' : item.success_rate > 0 ? 'text-amber-600' : 'text-stone-400'}`}>
                                    {item.success_rate}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" style={{ width: `${item.success_rate}%` }} />
                                  </div>
                                  <span className="text-xs text-stone-500">{item.catches}/{item.total} catches</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No material data available yet</p>
                      )}
                    </div>
                  )}
                  
                  {/* Lure Performance */}
                  {activePerformanceTab === 'lure' && (
                    <div>
                      <h4 className="font-semibold text-stone-700 mb-3">Lure Type Performance Analysis</h4>
                      {performanceData.by_lure?.length > 0 ? (
                        <div className="space-y-2">
                          {performanceData.by_lure.map((item, idx) => (
                            <div key={item.name} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                              <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🍋'}</span>
                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-stone-700">{item.name}</span>
                                  <span className={`text-sm font-bold ${item.success_rate > 50 ? 'text-green-600' : item.success_rate > 0 ? 'text-amber-600' : 'text-stone-400'}`}>
                                    {item.success_rate}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-lime-400 to-lime-600 rounded-full" style={{ width: `${item.success_rate}%` }} />
                                  </div>
                                  <span className="text-xs text-stone-500">{item.catches}/{item.total} catches</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No lure data available yet</p>
                      )}
                    </div>
                  )}
                  
                  {/* Location Performance */}
                  {activePerformanceTab === 'location' && (
                    <div>
                      <h4 className="font-semibold text-stone-700 mb-3">Location Performance Analysis</h4>
                      {performanceData.by_location?.length > 0 ? (
                        <div className="space-y-2">
                          {performanceData.by_location.map((item, idx) => (
                            <div key={item.name} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                              <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📍'}</span>
                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-stone-700">{item.name}</span>
                                  <span className={`text-sm font-bold ${item.success_rate > 50 ? 'text-green-600' : item.success_rate > 0 ? 'text-amber-600' : 'text-stone-400'}`}>
                                    {item.success_rate}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-teal-400 to-teal-600 rounded-full" style={{ width: `${item.success_rate}%` }} />
                                  </div>
                                  <span className="text-xs text-stone-500">{item.catches}/{item.total} catches</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No location data available yet</p>
                      )}
                    </div>
                  )}
                  
                  {/* Volume/Size Performance */}
                  {activePerformanceTab === 'volume' && (
                    <div>
                      <h4 className="font-semibold text-stone-700 mb-3">Trap Size Performance Analysis</h4>
                      {performanceData.by_volume?.length > 0 ? (
                        <div className="space-y-2">
                          {performanceData.by_volume.map((item, idx) => (
                            <div key={item.name} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                              <span className="text-lg">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📦'}</span>
                              <div className="flex-1">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-stone-700">{item.name}</span>
                                  <span className={`text-sm font-bold ${item.success_rate > 50 ? 'text-green-600' : item.success_rate > 0 ? 'text-amber-600' : 'text-stone-400'}`}>
                                    {item.success_rate}%
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full" style={{ width: `${item.success_rate}%` }} />
                                  </div>
                                  <span className="text-xs text-stone-500">{item.catches}/{item.total} catches</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No size data available yet</p>
                      )}
                      <p className="text-xs text-stone-400 mt-3">
                        📊 Research suggests 40-60L is the ideal trap volume, similar to natural cavity sizes preferred by scout bees.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Rescued Swarms Section */}
        <div className="mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-purple-800 flex items-center gap-2">
                🆘 Rescued Swarms
              </h3>
              <Button
                onClick={() => { resetRescueForm(); setShowRescueDialog(true); }}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Log Rescue
              </Button>
            </div>
            
            {/* Rescue Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white rounded-xl p-3 border border-purple-100 text-center shadow-sm">
                <div className="text-2xl font-bold text-purple-700">{rescuedSwarms.length}</div>
                <div className="text-xs text-purple-600">Total Rescues</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-100 text-center shadow-sm">
                <div className="text-2xl font-bold text-green-700">
                  {rescuedSwarms.filter(r => r.outcome === 'converted_to_hive').length}
                </div>
                <div className="text-xs text-green-600">Moved to Hive</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100 text-center shadow-sm">
                <div className="text-2xl font-bold text-blue-700">
                  {rescuedSwarms.filter(r => r.outcome === 'transferred_to_nuc').length}
                </div>
                <div className="text-xs text-blue-600">Moved to NUC</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-amber-100 text-center shadow-sm">
                <div className="text-2xl font-bold text-amber-700">
                  {rescuedSwarms.filter(r => new Date(r.date).getFullYear() === new Date().getFullYear()).length}
                </div>
                <div className="text-xs text-amber-600">This Season</div>
              </div>
            </div>
            
            {/* Rescued Swarms List */}
            {rescuedSwarms.length > 0 ? (
              <div className="space-y-3">
                {rescuedSwarms.slice(0, 5).map((rescue) => (
                  <div key={rescue.id} className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📍</span>
                          <h4 className="font-semibold text-stone-800">{rescue.location || 'Unknown Location'}</h4>
                        </div>
                        <div className="text-sm text-stone-500 mt-1 flex flex-wrap gap-3">
                          {rescue.date && <span>📅 {rescue.date}</span>}
                          {rescue.cluster_location && (
                            <span>🌳 {rescue.cluster_location}</span>
                          )}
                          {rescue.height_off_ground && <span>📏 {rescue.height_off_ground}</span>}
                        </div>
                        {rescue.outcome && (
                          <div className="mt-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              rescue.outcome === 'converted_to_hive' ? 'bg-green-100 text-green-700' :
                              rescue.outcome === 'transferred_to_nuc' ? 'bg-blue-100 text-blue-700' :
                              rescue.outcome === 'absconded' ? 'bg-red-100 text-red-700' :
                              'bg-stone-100 text-stone-700'
                            }`}>
                              {rescue.outcome === 'converted_to_hive' ? (
                                <>🏠 Moved to {(() => {
                                  const hive = hives.find(h => h.id === rescue.destination_hive_id);
                                  return hive ? (hive.nickname || `Hive ${hive.hive_number}`) : 'Hive';
                                })()}</>
                              ) : rescue.outcome === 'transferred_to_nuc' ? (
                                <>📦 Moved to {(() => {
                                  const hive = hives.find(h => h.id === rescue.destination_hive_id);
                                  return hive ? (hive.nickname || `NUC ${hive.hive_number}`) : 'NUC';
                                })()}</>
                              ) : rescue.outcome === 'absconded' ? '💨 Absconded' : '📋 Other'}
                            </span>
                          </div>
                        )}
                        {(rescue.photos?.length > 0 || rescue.videos?.length > 0) && (
                          <div className="flex gap-2 mt-2">
                            {rescue.photos?.slice(0, 3).map((url, idx) => (
                              <img key={idx} src={url} alt="" className="w-12 h-12 object-cover rounded-lg border border-purple-200" />
                            ))}
                          </div>
                        )}
                        {/* Update Outcome Button - shown when no outcome yet */}
                        {!rescue.outcome && (
                          <Button
                            onClick={() => {
                              setSelectedRescueForOutcome(rescue);
                              setOutcomeFormData({ outcome: '', destination_hive_id: '' });
                              setShowOutcomeDialog(true);
                            }}
                            className="mt-3 bg-purple-600 hover:bg-purple-700 text-white text-sm"
                            size="sm"
                          >
                            🏠 Add Outcome
                          </Button>
                        )}
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingRescue(rescue);
                            // Handle legacy "Other" pattern - use cluster_location_other if cluster_location was "Other"
                            const clusterLoc = rescue.cluster_location === 'Other' 
                              ? (rescue.cluster_location_other || '') 
                              : (rescue.cluster_location || '');
                            setRescueFormData({
                              location: rescue.location || '',
                              cluster_location: clusterLoc,
                              cluster_location_other: '', // No longer used
                              height_off_ground: rescue.height_off_ground || '',
                              date: rescue.date || '',
                              time: rescue.time || '',
                              notes: rescue.notes || '',
                              outcome: rescue.outcome || '',
                              destination_hive_id: rescue.destination_hive_id || '',
                              photos: rescue.photos || [],
                              videos: rescue.videos || []
                            });
                            setShowRescueDialog(true);
                          }}
                          className="h-8 w-8 text-purple-600 hover:bg-purple-100"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteRescuedSwarm(rescue.id)}
                          className="h-8 w-8 text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {rescuedSwarms.length > 5 && (
                  <p className="text-center text-sm text-purple-600">
                    Showing 5 of {rescuedSwarms.length} rescues
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 border border-purple-100 text-center">
                <div className="text-3xl mb-2">🐝</div>
                <p className="text-stone-600 font-medium">No rescued swarms logged yet</p>
                <p className="text-stone-400 text-sm mt-1">Log your swarm rescues to track where and when you find them</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || !!editingTrap} onOpenChange={() => { setShowAddDialog(false); setEditingTrap(null); resetForm(); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-stone-800 font-heading">
              {editingTrap ? 'Edit Swarm Trap' : 'Add Swarm Trap'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trap Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Trap #1"
                  className="h-12 mt-1"
                />
              </div>
              <div>
                <Label>Apiary</Label>
                <Select value={formData.yard} onValueChange={(v) => setFormData({ ...formData, yard: v })}>
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select apiary" />
                  </SelectTrigger>
                  <SelectContent>
                    {yards.map((y) => (
                      <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Tree at back of property"
                className="h-12 mt-1"
              />
            </div>

            {/* Dimensions */}
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <Label className="font-semibold text-stone-700">📐 Trap Dimensions</Label>
                <Select value={formData.measurement_system} onValueChange={(v) => setFormData({ ...formData, measurement_system: v })}>
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEASUREMENT_SYSTEMS.map((sys) => (
                      <SelectItem key={sys.value} value={sys.value}>{sys.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Width ({formData.measurement_system === 'imperial' ? 'in' : 'cm'})</Label>
                  <Input
                    type="number"
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                    placeholder="W"
                    className="h-10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Height ({formData.measurement_system === 'imperial' ? 'in' : 'cm'})</Label>
                  <Input
                    type="number"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    placeholder="H"
                    className="h-10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Length ({formData.measurement_system === 'imperial' ? 'in' : 'cm'})</Label>
                  <Input
                    type="number"
                    value={formData.length}
                    onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                    placeholder="L"
                    className="h-10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Volume (L) <span className="text-green-600">✓ Auto</span></Label>
                  <Input
                    value={formData.volume}
                    onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                    placeholder="Auto-calc"
                    className="h-10 mt-1 bg-green-50 border-green-200"
                    title="Auto-calculated from W×H×L"
                  />
                </div>
              </div>
            </div>

            {/* Material */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Material</Label>
                <Select value={formData.material} onValueChange={(v) => setFormData({ ...formData, material: v })}>
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent>
                    {MATERIALS.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.material === 'Other' && (
                  <Input
                    value={formData.material_other}
                    onChange={(e) => setFormData({ ...formData, material_other: e.target.value })}
                    placeholder="Specify material"
                    className="h-10 mt-2"
                  />
                )}
              </div>
              <div>
                <Label>Entrance Size</Label>
                <Input
                  value={formData.entrance_size}
                  onChange={(e) => setFormData({ ...formData, entrance_size: e.target.value })}
                  placeholder="e.g., 1.5 inch"
                  className="h-12 mt-1"
                />
              </div>
            </div>

            {/* Frames */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <Label className="font-semibold text-stone-700 mb-3 block">🖼️ Frames</Label>
              
              {/* Existing frame entries */}
              {formData.frame_entries?.length > 0 && (
                <div className="mb-3 space-y-2">
                  {formData.frame_entries.map((entry, idx) => (
                    <div key={idx} className="bg-white border border-amber-200 rounded-lg p-2 flex justify-between items-center">
                      <span className="text-sm font-medium">
                        {entry.count}x {entry.size} {entry.type}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newEntries = formData.frame_entries.filter((_, i) => i !== idx);
                          setFormData({ ...formData, frame_entries: newEntries });
                        }}
                        className="h-6 w-6 text-red-500"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Label className="text-xs">Frame Type</Label>
                  <Select value={formData.frame_type} onValueChange={(v) => setFormData({ ...formData, frame_type: v })}>
                    <SelectTrigger className="h-10 mt-1">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FRAME_TYPES.map((f) => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Frame Size</Label>
                  <Select value={formData.frame_size} onValueChange={(v) => setFormData({ ...formData, frame_size: v })}>
                    <SelectTrigger className="h-10 mt-1">
                      <SelectValue placeholder="Size" />
                    </SelectTrigger>
                    <SelectContent>
                      {FRAME_SIZES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs"># Frames</Label>
                  <Input
                    type="number"
                    value={formData.frame_count}
                    onChange={(e) => setFormData({ ...formData, frame_count: e.target.value })}
                    placeholder="0"
                    className="h-10 mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    onClick={() => {
                      if (formData.frame_type && formData.frame_size && formData.frame_count) {
                        const newEntry = {
                          type: formData.frame_type,
                          size: formData.frame_size,
                          count: formData.frame_count
                        };
                        setFormData({
                          ...formData,
                          frame_entries: [...(formData.frame_entries || []), newEntry],
                          frame_type: '',
                          frame_size: '',
                          frame_count: ''
                        });
                      } else {
                        toast.error('Please fill all frame fields');
                      }
                    }}
                    className="h-10 w-full bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Swarm Lure */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Swarm Lure</Label>
                <Select value={formData.swarm_lure} onValueChange={(v) => setFormData({ ...formData, swarm_lure: v })}>
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select lure" />
                  </SelectTrigger>
                  <SelectContent>
                    {LURE_TYPES.map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.swarm_lure === 'Other' && (
                  <Input
                    value={formData.swarm_lure_other}
                    onChange={(e) => setFormData({ ...formData, swarm_lure_other: e.target.value })}
                    placeholder="Specify lure"
                    className="h-10 mt-2"
                  />
                )}
              </div>
            </div>

            {/* Date Placed */}
            <div>
              <Label>Date Placed</Label>
              <Input
                type="date"
                value={formData.date_placed}
                onChange={(e) => setFormData({ ...formData, date_placed: e.target.value })}
                className="h-12 mt-1"
              />
              <p className="text-xs text-stone-400 mt-1">When was this trap deployed? Helps track time-to-catch.</p>
            </div>

            {/* Activity Records */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <Label className="font-semibold text-stone-700 mb-3 block">🐝 Record of Activity</Label>
              
              {formData.activity_records.length > 0 && (
                <div className="mb-3 space-y-2">
                  {formData.activity_records.map((activity) => (
                    <div key={activity.id} className="bg-white border border-blue-200 rounded-lg p-2 flex justify-between items-center">
                      <div className="text-sm">
                        <span className="font-medium">{activity.date}</span> {activity.time} - {activity.scouts_level} scouts
                        {activity.notes && <span className="text-stone-500 ml-2">({activity.notes})</span>}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeActivityRecord(activity.id)} className="h-6 w-6 text-red-500">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-4 gap-2">
                <Input
                  type="date"
                  value={newActivity.date}
                  onChange={(e) => setNewActivity({ ...newActivity, date: e.target.value })}
                  className="h-10"
                />
                <Input
                  type="time"
                  value={newActivity.time}
                  onChange={(e) => setNewActivity({ ...newActivity, time: e.target.value })}
                  className="h-10"
                />
                <Select value={newActivity.scouts_level} onValueChange={(v) => setNewActivity({ ...newActivity, scouts_level: v })}>
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder="Scouts" />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOUT_LEVELS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addActivityRecord} className="h-10 bg-blue-600 hover:bg-blue-700 text-white">
                  Add
                </Button>
              </div>
            </div>

            {/* Swarm Arrival */}
            <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
              <Label className="font-semibold text-stone-700 mb-3 block">✅ Swarm Arrived</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    value={formData.swarm_arrived_date}
                    onChange={(e) => setFormData({ ...formData, swarm_arrived_date: e.target.value })}
                    className="h-10 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Time</Label>
                  <Input
                    type="time"
                    value={formData.swarm_arrived_time}
                    onChange={(e) => setFormData({ ...formData, swarm_arrived_time: e.target.value })}
                    className="h-10 mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 sm:gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => { setShowAddDialog(false); setEditingTrap(null); resetForm(); }}
                className="flex-1 h-12 sm:h-14 rounded-full text-sm sm:text-base"
              >
                Cancel
              </Button>
              {editingTrap && (
                <Button
                  variant="destructive"
                  onClick={() => { handleDeleteTrap(editingTrap.id); setEditingTrap(null); }}
                  className="h-12 sm:h-14 px-4 sm:px-6 rounded-full"
                >
                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              )}
              <Button
                onClick={editingTrap ? handleUpdateTrap : handleAddTrap}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-12 sm:h-14 rounded-full font-bold text-sm sm:text-base"
              >
                {editingTrap ? 'Save' : 'Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rescued Swarm Dialog */}
      <Dialog open={showRescueDialog} onOpenChange={setShowRescueDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-800">
              🆘 {editingRescue ? 'Edit Rescue Record' : 'Log Swarm Rescue'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Location */}
            <div>
              <Label className="text-sm font-medium">Location / Address *</Label>
              <Input
                value={rescueFormData.location}
                onChange={(e) => setRescueFormData({ ...rescueFormData, location: e.target.value })}
                placeholder="Where was the swarm rescued from?"
                className="h-10 mt-1"
              />
            </div>
            
            {/* Cluster found in/on */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Cluster found in/on</Label>
                <Input
                  value={rescueFormData.cluster_location}
                  onChange={(e) => setRescueFormData({ ...rescueFormData, cluster_location: e.target.value })}
                  placeholder="Type or select location..."
                  list="cluster-locations-list"
                  className="h-10 mt-1"
                />
                <datalist id="cluster-locations-list">
                  {CLUSTER_LOCATIONS.filter(loc => loc !== 'Other').map((loc) => (
                    <option key={loc} value={loc} />
                  ))}
                </datalist>
                <p className="text-xs text-stone-400 mt-1">
                  Type custom location or select from suggestions
                </p>
              </div>
              <div>
                <Label className="text-sm">Height off ground</Label>
                <Select 
                  value={rescueFormData.height_off_ground} 
                  onValueChange={(v) => setRescueFormData({ ...rescueFormData, height_off_ground: v })}
                >
                  <SelectTrigger className="h-10 mt-1">
                    <SelectValue placeholder="Select height" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEIGHT_OPTIONS.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date</Label>
                <Input
                  type="date"
                  value={rescueFormData.date}
                  onChange={(e) => setRescueFormData({ ...rescueFormData, date: e.target.value })}
                  className="h-10 mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Time</Label>
                <Input
                  type="time"
                  value={rescueFormData.time}
                  onChange={(e) => setRescueFormData({ ...rescueFormData, time: e.target.value })}
                  className="h-10 mt-1"
                />
              </div>
            </div>
            
            {/* Outcome - Only shown when editing */}
            {editingRescue && (
              <>
                <div>
                  <Label className="text-sm">Outcome</Label>
                  <Select 
                    value={rescueFormData.outcome} 
                    onValueChange={(v) => setRescueFormData({ ...rescueFormData, outcome: v })}
                  >
                    <SelectTrigger className="h-10 mt-1">
                      <SelectValue placeholder="What happened to the swarm?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="converted_to_hive">🏠 Moved to Hive</SelectItem>
                      <SelectItem value="transferred_to_nuc">📦 Moved to NUC</SelectItem>
                      <SelectItem value="absconded">💨 Absconded</SelectItem>
                      <SelectItem value="released">🌳 Released</SelectItem>
                      <SelectItem value="pending">⏳ Pending / In Progress</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Destination Hive (if moved) */}
                {(rescueFormData.outcome === 'converted_to_hive' || rescueFormData.outcome === 'transferred_to_nuc') && (
                  <div>
                    <Label className="text-sm">Destination Hive/NUC</Label>
                    <Select 
                      value={rescueFormData.destination_hive_id} 
                      onValueChange={(v) => setRescueFormData({ ...rescueFormData, destination_hive_id: v })}
                    >
                      <SelectTrigger className="h-10 mt-1">
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        {hives.map((hive) => (
                          <SelectItem key={hive.id} value={hive.id}>{hive.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            
            {/* Notes */}
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea
                value={rescueFormData.notes}
                onChange={(e) => setRescueFormData({ ...rescueFormData, notes: e.target.value })}
                placeholder="Any additional details about the rescue..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            {/* Photo & Video Capture */}
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <Label className="text-sm font-semibold text-purple-800 mb-2 block">📸 Photos & Videos</Label>
              <div className="flex gap-2 flex-wrap">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleRescueMediaUpload(e, 'photos')}
                    className="hidden"
                  />
                  <div className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-full text-sm font-medium transition-colors">
                    <Camera className="h-4 w-4" />
                    Camera
                  </div>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleRescueMediaUpload(e, 'photos')}
                    className="hidden"
                  />
                  <div className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-full text-sm font-medium transition-colors">
                    <Image className="h-4 w-4" />
                    Photo
                  </div>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    capture="environment"
                    onChange={(e) => handleRescueMediaUpload(e, 'videos')}
                    className="hidden"
                  />
                  <div className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-full text-sm font-medium transition-colors">
                    <Video className="h-4 w-4" />
                    Record
                  </div>
                </label>
              </div>
              
              {/* Display uploaded media */}
              {rescueFormData.photos?.length > 0 && (
                <div className="mt-3">
                  <Label className="text-xs text-purple-700">Photos ({rescueFormData.photos.length})</Label>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {rescueFormData.photos.map((url, idx) => (
                      <div key={idx} className="relative">
                        <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-purple-300" />
                        <button
                          type="button"
                          onClick={() => removeRescueMedia('photos', idx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rescueFormData.videos?.length > 0 && (
                <div className="mt-3">
                  <Label className="text-xs text-purple-700">Videos ({rescueFormData.videos.length})</Label>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {rescueFormData.videos.map((url, idx) => (
                      <div key={idx} className="relative">
                        <video src={url} className="w-20 h-16 object-cover rounded-lg border-2 border-purple-300" />
                        <button
                          type="button"
                          onClick={() => removeRescueMedia('videos', idx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setShowRescueDialog(false); resetRescueForm(); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveRescuedSwarm}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                {editingRescue ? 'Save Changes' : 'Log Rescue'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Outcome Dialog */}
      <Dialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-800">
              🏠 Update Outcome
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {selectedRescueForOutcome && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <p className="text-sm text-purple-800">
                  <strong>📍 {selectedRescueForOutcome.location}</strong>
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Rescued on {selectedRescueForOutcome.date}
                </p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium">What happened to the swarm?</Label>
              <Select 
                value={outcomeFormData.outcome} 
                onValueChange={(v) => setOutcomeFormData({ ...outcomeFormData, outcome: v })}
              >
                <SelectTrigger className="h-12 mt-2">
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="converted_to_hive">🏠 Moved to Hive</SelectItem>
                  <SelectItem value="transferred_to_nuc">📦 Moved to NUC</SelectItem>
                  <SelectItem value="absconded">💨 Absconded</SelectItem>
                  <SelectItem value="released">🌳 Released</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(outcomeFormData.outcome === 'converted_to_hive' || outcomeFormData.outcome === 'transferred_to_nuc') && (
              <div>
                <Label className="text-sm font-medium">
                  Select Destination {outcomeFormData.outcome === 'transferred_to_nuc' ? 'NUC' : 'Hive'}
                </Label>
                <div className="flex gap-2 mt-2">
                  <Select 
                    value={outcomeFormData.destination_hive_id} 
                    onValueChange={async (v) => {
                      // Auto-save when hive is selected
                      if (v && selectedRescueForOutcome) {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.put(`${API}/rescued-swarms/${selectedRescueForOutcome.id}`, {
                            ...selectedRescueForOutcome,
                            outcome: outcomeFormData.outcome,
                            destination_hive_id: v
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          
                          toast.success(`Swarm moved to ${hives.find(h => h.id === v)?.nickname || hives.find(h => h.id === v)?.name || 'hive'}`);
                          setShowOutcomeDialog(false);
                          setSelectedRescueForOutcome(null);
                          setOutcomeFormData({ outcome: '', destination_hive_id: '' });
                          fetchData();
                        } catch (error) {
                          toast.error('Failed to save outcome');
                          setOutcomeFormData({ ...outcomeFormData, destination_hive_id: v });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-12 flex-1">
                      <SelectValue placeholder={`Select ${outcomeFormData.outcome === 'transferred_to_nuc' ? 'NUC' : 'hive'} (auto-saves)`} />
                    </SelectTrigger>
                    <SelectContent>
                      {hives.length === 0 ? (
                        <div className="p-3 text-center text-stone-500 text-sm">
                          No {outcomeFormData.outcome === 'transferred_to_nuc' ? 'NUCs' : 'hives'} available
                        </div>
                      ) : (
                        hives.map((hive) => (
                          <SelectItem key={hive.id} value={hive.id}>
                            {hive.nickname || `Hive ${hive.hive_number}`}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setQuickAddHiveData({
                        name: '',
                        type: outcomeFormData.outcome === 'transferred_to_nuc' ? 'nuc' : 'hive',
                        yard: yards[0]?.name || ''
                      });
                      setShowQuickAddHive(true);
                    }}
                    className="h-12 px-4 border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    New
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  ✓ Selecting a {outcomeFormData.outcome === 'transferred_to_nuc' ? 'NUC' : 'hive'} will automatically save.
                </p>
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => { setShowOutcomeDialog(false); setSelectedRescueForOutcome(null); setOutcomeFormData({ outcome: '', destination_hive_id: '' }); }}
                className="flex-1"
              >
                Cancel
              </Button>
              {/* Only show Save button for non-hive outcomes (absconded, released) */}
              {outcomeFormData.outcome && outcomeFormData.outcome !== 'converted_to_hive' && outcomeFormData.outcome !== 'transferred_to_nuc' && (
                <Button
                  onClick={saveOutcome}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Save Outcome
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Hive/NUC Dialog */}
      <Dialog open={showQuickAddHive} onOpenChange={setShowQuickAddHive}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-800">
              {quickAddHiveData.type === 'nuc' ? '📦 Create New NUC' : '🏠 Create New Hive'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="bg-green-50 rounded-lg p-3 border border-green-200 text-sm text-green-700">
              ✓ Create a new {quickAddHiveData.type === 'nuc' ? 'NUC' : 'hive'} and automatically save the outcome
            </div>
            
            {/* Suggested next number */}
            {(() => {
              const maxHiveNum = Math.max(0, ...hives.map(h => h.hive_number || 0));
              const nextNum = maxHiveNum + 1;
              const isNuc = quickAddHiveData.type === 'nuc';
              return (
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-amber-700 mb-2">💡 Suggested next available:</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuickAddHiveData({ ...quickAddHiveData, name: isNuc ? `NUC ${nextNum}` : `Hive ${nextNum}` })}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      {isNuc ? `NUC ${nextNum}` : `Hive ${nextNum}`}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuickAddHiveData({ ...quickAddHiveData, name: `Swarm ${nextNum}` })}
                      className="text-amber-700 border-amber-300 hover:bg-amber-100"
                    >
                      Swarm {nextNum}
                    </Button>
                  </div>
                </div>
              );
            })()}
            
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input
                value={quickAddHiveData.name}
                onChange={(e) => setQuickAddHiveData({ ...quickAddHiveData, name: e.target.value })}
                placeholder={quickAddHiveData.type === 'nuc' ? 'e.g., NUC #5, Swarm NUC 1' : 'e.g., Hive 10, New Colony'}
                className="h-12 mt-2"
              />
            </div>
            
            <div>
              <Label className="text-sm font-medium">Location / Yard</Label>
              <Select 
                value={quickAddHiveData.yard} 
                onValueChange={(v) => setQuickAddHiveData({ ...quickAddHiveData, yard: v })}
              >
                <SelectTrigger className="h-12 mt-2">
                  <SelectValue placeholder="Select yard" />
                </SelectTrigger>
                <SelectContent>
                  {yards.map((yard) => (
                    <SelectItem key={yard.id || yard.name} value={yard.name}>{yard.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowQuickAddHive(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveQuickAddHive}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={!quickAddHiveData.name}
              >
                Create & Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <QRCodeGenerator
        open={showQRDialog}
        onClose={() => setShowQRDialog(false)}
        type="swarm-trap"
        id={selectedTrap?.id}
        name={selectedTrap?.name}
      />

      {/* Move to Hive Dialog */}
      {showMoveToHiveDialog && moveToHiveTrap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowMoveToHiveDialog(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-5">
              <h3 className="font-bold text-lg">Move Swarm to Hive</h3>
              <p className="text-amber-100 text-sm mt-1">
                From trap: <strong>{moveToHiveTrap.name}</strong>
              </p>
            </div>
            <div className="p-5 space-y-4">
              {readyHives.length > 0 && (
                <div>
                  <h4 className="font-semibold text-stone-800 text-sm mb-2">Assign to a Ready Hive</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {readyHives.map(h => (
                      <button
                        key={h.id}
                        onClick={() => handleAssignToReadyHive(h.id)}
                        className="w-full text-left p-3 rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-center justify-between"
                      >
                        <div>
                          <span className="font-medium text-stone-800">Hive #{h.hive_number}</span>
                          {h.nickname && <span className="text-stone-500 ml-2">({h.nickname})</span>}
                          <div className="text-xs text-stone-400">{h.yard} — {h.hive_type || 'Unknown type'}</div>
                        </div>
                        <span className="px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded-full">Ready</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-stone-200 pt-4">
                <Button
                  onClick={() => {
                    setShowMoveToHiveDialog(false);
                    navigate('/', { state: { openAddHive: true, trapData: moveToHiveTrap } });
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Hive Instead
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowMoveToHiveDialog(false)}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwarmTraps;
